import { MagnifyingGlassIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Decimal } from 'decimal.js';
import { useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import type { ItemDisplayColumn } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { ProductThumbnail } from '@/components/product-thumbnail';
import { useShowPrices } from '@/contexts/auth';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { formatYMD, formatYMDSlash, isValidYMD, MAX_DATE_INPUT } from '@/utils/date';
import {
  formatItemBreakdown,
  formatItemMeta,
  formatYen,
  getPrimaryPackaging,
  type ItemColumnVisibility,
  toItemColumnVisibility,
} from './item-display';
import type { OrderFormBase } from './schema';
import type { OrderItemDisplayData } from './types';

export type ItemFilter = 'all' | 'entered';

interface QuantityStepperProps {
  index: number;
  quantity: number;
  size: 'md' | 'lg';
}

const QuantityStepper = ({ index, quantity, size }: QuantityStepperProps) => {
  const { register, setValue } = useFormContext<OrderFormBase>();
  const path = `items.${index}.quantity` as const;
  const change = (next: number) =>
    setValue(path, Math.max(0, next), { shouldDirty: true, shouldValidate: true });
  const buttonClassName = clsx(
    'flex shrink-0 cursor-pointer items-center justify-center bg-neutral-100 hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-40',
    size === 'lg' ? 'size-11' : 'size-10',
  );

  return (
    <div className="flex items-center overflow-hidden rounded border border-neutral-400">
      <button
        type="button"
        aria-label="数量を減らす"
        className={buttonClassName}
        disabled={quantity <= 0}
        onClick={() => change(new Decimal(quantity).minus(1).toNumber())}
      >
        <MinusIcon className="size-5" />
      </button>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="any"
        aria-label="数量"
        className={clsx('w-16! border-0! px-1! text-center font-bold', size === 'lg' ? 'h-11' : 'h-10')}
        // 入力を消している途中は0として扱う(NaN のままだと確認へ進めず、絞り込みからも外れる)
        {...register(path, { setValueAs: (value: string) => (value === '' ? 0 : Number(value)) })}
      />
      <button
        type="button"
        aria-label="数量を増やす"
        className={buttonClassName}
        onClick={() => change(new Decimal(quantity).plus(1).toNumber())}
      >
        <PlusIcon className="size-5" />
      </button>
    </div>
  );
};

const AddPackagingButton = ({ index, item }: { index: number; item: OrderItemDisplayData }) => {
  const { setValue } = useFormContext<OrderFormBase>();
  const packaging = getPrimaryPackaging(item);
  if (!packaging) return null;
  return (
    <button
      type="button"
      onClick={() =>
        setValue(`items.${index}.quantity`, new Decimal(item.quantity).plus(packaging.factor).toNumber(), {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
      className="h-11 shrink-0 cursor-pointer rounded border border-sky-800 bg-white px-2.5 text-sm font-bold whitespace-nowrap text-sky-800 hover:bg-sky-50 md:h-10"
    >
      ＋1{packaging.name}
    </button>
  );
};

const LineDeliveryDateInput = ({ index }: { index: number }) => {
  const { control } = useFormContext<OrderFormBase>();
  return (
    <Controller
      control={control}
      name={`items.${index}.desired_delivery_date`}
      render={({ field }) => (
        <input
          type="date"
          max={MAX_DATE_INPUT}
          aria-label="明細の希望納期"
          className="h-10"
          value={field.value ? formatYMD(field.value, '') : ''}
          onChange={(e) =>
            field.onChange(
              isValidYMD(e.target.value) ? new Date(`${e.target.value}T00:00:00`).toISOString() : null,
            )
          }
        />
      )}
    />
  );
};

const PreviousPriceNote = ({ item }: { item: OrderItemDisplayData }) => {
  if (!item.previousUnitPrice || new Decimal(item.previousUnitPrice).equals(item.unitPrice)) return null;
  return <span className="text-sm text-amber-700">（前回 ¥{formatYen(item.previousUnitPrice)}）</span>;
};

/** 料金を表示しない発注先では単価の横に出していた単位を数量の横に出す */
const QuantityUnit = ({ item }: { item: OrderItemDisplayData }) =>
  item.unitLabel ? (
    <span className="text-sm whitespace-nowrap text-neutral-600">{item.unitLabel}</span>
  ) : null;

interface RowProps {
  index: number;
  item: OrderItemDisplayData;
  visibility: ItemColumnVisibility;
  showLastPurchased: boolean;
  showPrices: boolean;
}

const ItemCard = ({ index, item, visibility, showLastPurchased, showPrices }: RowProps) => {
  const meta = formatItemMeta(item, visibility);
  const breakdown = formatItemBreakdown(item);
  const hasQuantity = item.quantity > 0;
  return (
    <li
      className={clsx(
        'flex flex-col gap-3 border bg-white p-3',
        hasQuantity ? 'border-sky-800 outline outline-sky-800' : 'border-neutral-200',
      )}
    >
      <div className="flex gap-3">
        <ProductThumbnail url={item.thumbnailUrl} size={64} />
        <div className="flex min-w-0 flex-1 flex-col leading-snug">
          <span className="font-bold">{item.name}</span>
          {meta && <span className="text-sm text-neutral-600">{meta}</span>}
          {showPrices ? (
            <span className="mt-1 text-sm">
              <span className="text-base font-bold">¥{formatYen(item.unitPrice)}</span>
              {item.unitLabel && <span className="text-neutral-600"> / {item.unitLabel}</span>}
              <PreviousPriceNote item={item} />
              {visibility.snp && <span className="ml-2 text-sm text-neutral-600">入数 {item.snp}</span>}
            </span>
          ) : (
            visibility.snp && <span className="mt-1 text-sm text-neutral-600">入数 {item.snp}</span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        {showLastPurchased && (
          <span className="mr-auto text-sm text-neutral-600">
            {item.lastPurchasedAt ? `最終購入 ${formatYMDSlash(item.lastPurchasedAt)}` : ''}
          </span>
        )}
        <AddPackagingButton index={index} item={item} />
        <QuantityStepper index={index} quantity={item.quantity} size="lg" />
        {!showPrices && <QuantityUnit item={item} />}
      </div>
      {visibility.deliveryDate && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-neutral-600">希望納期</span>
          <div className="w-44">
            <LineDeliveryDateInput index={index} />
          </div>
        </div>
      )}
      {hasQuantity && (showPrices || breakdown) && (
        <div className="flex justify-between border-t border-dashed border-neutral-200 pt-2 text-sm">
          <span className="text-neutral-600">{breakdown}</span>
          {showPrices && <span className="font-bold">{formatYen(item.amount)} 円</span>}
        </div>
      )}
    </li>
  );
};

const ItemTableRow = ({ index, item, visibility, showLastPurchased, showPrices }: RowProps) => {
  const meta = formatItemMeta(item, visibility);
  const breakdown = formatItemBreakdown(item);
  return (
    <tr className={clsx('border-t border-neutral-200', item.quantity > 0 && 'bg-sky-50')}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-3">
          <ProductThumbnail url={item.thumbnailUrl} size={44} />
          <div className="flex min-w-0 flex-col leading-snug">
            <span className="font-bold break-words">{item.name}</span>
            {meta && <span className="text-sm text-neutral-600">{meta}</span>}
          </div>
        </div>
      </td>
      {showPrices && (
        <td className="px-3 py-2 text-right">
          ¥{formatYen(item.unitPrice)}
          {item.unitLabel && <span className="text-sm text-neutral-600"> / {item.unitLabel}</span>}
          <div>
            <PreviousPriceNote item={item} />
          </div>
        </td>
      )}
      {visibility.snp && <td className="px-3 py-2 text-right">{item.snp}</td>}
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <QuantityStepper index={index} quantity={item.quantity} size="md" />
          {!showPrices && <QuantityUnit item={item} />}
          <AddPackagingButton index={index} item={item} />
        </div>
        {breakdown && <div className="mt-0.5 truncate text-sm text-neutral-600">{breakdown}</div>}
      </td>
      {showPrices && (
        <td className="px-3 py-2 text-right font-bold whitespace-nowrap">{formatYen(item.amount)} 円</td>
      )}
      {visibility.deliveryDate && (
        <td className="px-3 py-2">
          <LineDeliveryDateInput index={index} />
        </td>
      )}
      {showLastPurchased && (
        <td className="px-3 py-2 text-center whitespace-nowrap text-neutral-600">
          {formatYMDSlash(item.lastPurchasedAt)}
        </td>
      )}
    </tr>
  );
};

const matchesKeyword = (item: OrderItemDisplayData, keyword: string) => {
  if (!keyword) return true;
  const normalized = keyword.toLowerCase();
  return [item.name, item.productCode, item.modelNumber, item.spec, item.manufacturer].some((value) =>
    value?.toLowerCase().includes(normalized),
  );
};

interface OrderItemsEditorProps {
  items: OrderItemDisplayData[];
  itemDisplayColumns?: readonly ItemDisplayColumn[];
  emptyMessage: string;
  initialFilter?: ItemFilter;
}

/**
 * 明細の数量を入力する一覧。キーワード検索と「数量を入れた商品」の絞り込みは画面内で行う。
 * PC は表、スマホはカードで描画する。
 */
export const OrderItemsEditor = ({
  items,
  itemDisplayColumns,
  emptyMessage,
  initialFilter = 'all',
}: OrderItemsEditorProps) => {
  const { control } = useFormContext<OrderFormBase>();
  const { fields } = useFieldArray({ control, name: 'items', keyName: 'fieldId' });
  const isDesktop = useIsDesktop();
  const showPrices = useShowPrices();
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState<ItemFilter>(initialFilter);

  const visibility = useMemo(() => toItemColumnVisibility(itemDisplayColumns), [itemDisplayColumns]);
  const showLastPurchased = items.some((item) => item.lastPurchasedAt);
  // 商品・数量は常に表示し、単価・金額は料金の表示設定、残りは表示設定・データの有無で出し分ける
  const tableColumnCount =
    2 +
    2 * Number(showPrices) +
    Number(visibility.snp) +
    Number(visibility.deliveryDate) +
    Number(showLastPurchased);
  const enteredCount = items.filter((item) => item.quantity > 0).length;

  // 「数量を入れた商品」で表示した明細は、数量を0にしたり書き直したりしても絞り込みを切り替えるまで残す
  const keptEnteredIndexes = useRef(new Set<number>());
  const changeFilter = (next: ItemFilter) => {
    keptEnteredIndexes.current.clear();
    setFilter(next);
  };

  const visibleRows = fields
    .map((field, index) => ({ field, index, item: items[index] }))
    .filter(
      ({ index, item }) =>
        item &&
        (filter === 'all' || item.quantity > 0 || keptEnteredIndexes.current.has(index)) &&
        matchesKeyword(item, keyword),
    );
  if (filter === 'entered') {
    for (const { index } of visibleRows) keptEnteredIndexes.current.add(index);
  }

  const chips: { value: ItemFilter; label: string }[] = [
    { value: 'all', label: `すべての商品 ${items.length}` },
    { value: 'entered', label: `数量を入れた商品 ${enteredCount}` },
  ];

  const emptyText = fields.length === 0 ? emptyMessage : '該当する商品がありません';

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <label className="flex h-11 flex-1 items-center gap-2 border border-neutral-300 bg-white px-3 focus-within:border-sky-800 md:h-10">
          <MagnifyingGlassIcon className="size-5 shrink-0 text-neutral-400" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="商品名・商品コード・品番で検索"
            aria-label="商品を検索"
            className="border-0! px-0!"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              aria-pressed={filter === chip.value}
              onClick={() => changeFilter(chip.value)}
              className={clsx(
                'h-11 cursor-pointer rounded border px-3.5 text-sm whitespace-nowrap duration-300 md:h-10',
                filter === chip.value
                  ? 'border-sky-800 bg-sky-800 font-bold text-white'
                  : 'border-neutral-300 bg-white hover:bg-neutral-100',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {isDesktop ? (
        <div className="overflow-x-auto border border-neutral-200 bg-white">
          <table className="w-full min-w-[760px] table-fixed border-collapse text-sm tabular-nums">
            <colgroup>
              <col />
              {showPrices && <col className="w-32" />}
              {visibility.snp && <col className="w-16" />}
              {/* 料金を表示しない発注先は数量の横に単位を出すので広げる */}
              <col className={showPrices ? 'w-64' : 'w-72'} />
              {showPrices && <col className="w-32" />}
              {visibility.deliveryDate && <col className="w-40" />}
              {showLastPurchased && <col className="w-28" />}
            </colgroup>
            <thead>
              <tr className="bg-neutral-100 text-left text-neutral-600">
                <th className="px-3 py-2 font-normal">商品</th>
                {showPrices && <th className="px-3 py-2 text-right font-normal">単価</th>}
                {visibility.snp && <th className="px-3 py-2 text-right font-normal">入数</th>}
                <th className="px-3 py-2 font-normal">数量</th>
                {showPrices && <th className="px-3 py-2 text-right font-normal">金額</th>}
                {visibility.deliveryDate && <th className="px-3 py-2 font-normal">希望納期</th>}
                {showLastPurchased && <th className="px-3 py-2 text-center font-normal">最終購入日</th>}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(({ field, index, item }) => (
                <ItemTableRow
                  key={field.fieldId}
                  index={index}
                  item={item}
                  visibility={visibility}
                  showLastPurchased={showLastPurchased}
                  showPrices={showPrices}
                />
              ))}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={tableColumnCount} className="py-6 text-center text-neutral-600">
                    {emptyText}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {visibleRows.map(({ field, index, item }) => (
            <ItemCard
              key={field.fieldId}
              index={index}
              item={item}
              visibility={visibility}
              showLastPurchased={showLastPurchased}
              showPrices={showPrices}
            />
          ))}
          {visibleRows.length === 0 && <li className="py-6 text-center text-neutral-600">{emptyText}</li>}
        </ul>
      )}
    </section>
  );
};
